// Top-level app layout — topbar + sidebar skeleton.
//
// Nav items are split into:
//   - MAIN_NAV_ITEMS:        visible to every authenticated role.
//   - IDENTITY_NAV_ITEMS:    "Identity & Access" section — Groups & users
//                            entry that hosts Groups, Users, and the rule
//                            wizard launchers. Mirrors mockups/access-
//                            preview-v6.html.
//   - ADMIN_NAV_ITEMS:       Roles, Rules, Locations, Audit, Workspace.
//                            All gated by canSeeAdmin (FLEET_ADMIN today).
//
// Visual chrome (Ring-themed):
//   - Authentic Ring wordmark SVG at the top-left, paired with the
//     "Business" sub-label divided by a thin rule.
//   - Sidebar is a 64px collapsed rail that springs to 248px on hover
//     or focus-within. A pin button (visible only when the rail is
//     expanded) toggles the persistent expanded state, persisted in
//     localStorage under reb.ui.prefs.sidebarPinned.
//   - Each nav item carries a Ring-style currentColor SVG icon plus a
//     label that fades in as the rail expands. Active item picks up
//     Ring blue (var(--ring-primary-500)) on a tint background.
//
// Role-selection integration:
//   - <RoleSwitcher/> mounts in the topbar actions slot; self-hides when
//     `distinct_roles.length <= 1`.
//   - <RolePickerModal/> mounts at layout level; self-renders only when
//     role status is "pending". While pending, nav links and the main
//     content are marked inert/aria-hidden so interaction is blocked until
//     a role is picked.
//
// Advanced Mode surface:
//   - ADV badge appears next to the avatar menu when isAdvanced=true
//     (R5.2). It uses --semantic-warning-subtle background + --semantic-
//     warning text per the design token map.
//   - <AvatarMenu/> owns the Advanced Mode toggle + logout. This replaces
//     the previous bare logout icon button.
//
// <MockedDepsBanner/> is pinned bottom-left above the sidebar footer.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { api, ApiError, clearCurrentRole } from "@/api";
import {
  AvatarMenu,
  ErrorBoundary,
  MockedDepsBanner,
  RolePickerModal,
  RoleSwitcher,
  useToast,
} from "@/components";
// Ring App Store integration chrome — workspace pill in the topbar, post-
// install welcome banner above the page, and the non-dismissible
// disconnected modal at the layout root.
import { WelcomeBanner } from "@/components/chrome/WelcomeBanner";
import { WorkspaceIndicator } from "@/components/chrome/WorkspaceIndicator";
import { WorkspaceDisconnectedModal } from "@/components/chrome/WorkspaceDisconnectedModal";
import { useAdvancedMode } from "@/context/AdvancedModeContext";
import { useRole } from "@/role";
import { capabilitiesFor } from "@/role/capabilities";

interface NavItem {
  to: string;
  label: string;
  /** Inline Ring-style SVG. Drawn with `fill: currentColor` so the icon
   *  inherits the active/hover color from the surrounding link. */
  icon: ReactNode;
}

// Icons inlined as small JSX trees so the entire admin layout ships
// without any new asset dependency. Source paths are simplified versions
// of the Ring icon set in /mockups/ring-icons; using `currentColor` (set
// by .admin-sidebar__nav-icon) means hover and active states recolor
// without touching the markup.
const Icon = {
  camera: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17 4H7a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3zm0 1a2 2 0 0 1 2 2v2.382l-4 2V8.5a.5.5 0 0 0-.223-.416l-3.5-2.333a.5.5 0 0 0-.554 0l-3.5 2.333A.5.5 0 0 0 7 8.5V18a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zm.5 13.5a.5.5 0 0 1-.5.5H8V8.768l3-2 3 2v2.85l4 2v6.382z" />
    </svg>
  ),
  play: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 19a9 9 0 1 1 9-9 9 9 0 0 1-9 9zM10 8v8l6-4-6-4z" />
    </svg>
  ),
  account: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 11.5C10.067 11.5 8.5 9.933 8.5 8S10.067 4.5 12 4.5 15.5 6.067 15.5 8 13.933 11.5 12 11.5zm0-6C10.619 5.5 9.5 6.619 9.5 8s1.119 2.5 2.5 2.5S14.5 9.381 14.5 8 13.381 5.5 12 5.5zM12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 1c4.965.008 8.987 4.034 8.987 9 0 1.864-.567 3.595-1.537 5.032C17.831 14.586 15.099 13.02 12 13.02c-3.099 0-5.831 1.566-7.45 3.949A8.98 8.98 0 0 1 3.013 12c0-4.966 4.023-8.992 8.987-9zm0 18c-2.724-.006-5.164-1.222-6.81-3.138C6.61 15.54 9.13 14.024 12.005 14.024c2.875 0 5.395 1.516 6.805 3.792C17.171 19.781 14.728 20.997 12 21z"
      />
    </svg>
  ),
  integrations: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21.6 6.3l-3.9-3.9c-.3-.3-.7-.4-1.1-.4-.4 0-.8.1-1.1.4L11.7 6.2V5.9c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-5.9c0-1.1-.9-2-2-2h-.5l4.1-4.1c.6-.6.6-1.1 0-1.7zM10.5 20H4v-6.5h6.5V20zm0-7.5H3.5V4h7v8.5zm7 7.5H11.5v-6.5h6V20zm.5-7.5h-5.8l-.1-4.1 4.2 4.1zM20.8 7l-3.9 3.9c-.4.4-1 .4-1.4 0l-3.9-3.9c-.4-.4-.4-1 0-1.4l3.9-3.9c.4-.4 1-.4 1.4 0l3.9 3.9c.4.4.4 1 0 1.4z" />
    </svg>
  ),
  // "Secure" = lock-with-shield. Ring uses this for permissions surfaces.
  secure: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.5 9a.5.5 0 0 0-.35.15L10.68 13.61 8.86 11.79a.5.5 0 0 0-.71.71l2.5 2.5 5.19-5.15a.5.5 0 0 0-.34-.85z"
      />
      <path d="M21 16v-1c0-.82-.4-1.57-1.5-2.08V6.42A13.7 13.7 0 0 0 16.06 4.91 20.5 20.5 0 0 0 12 4a20.5 20.5 0 0 0-4.06.87A13.7 13.7 0 0 0 4.5 6.38V15a8.5 8.5 0 0 0 2.76 3.65A14.3 14.3 0 0 0 12 21c.68-.21 1.35-.47 2-.78.05.23.18.43.36.57.18.14.4.21.64.21h6c.27 0 .52-.1.71-.29.19-.19.29-.44.29-.71v-3c0-.27-.1-.52-.29-.71a1 1 0 0 0-.71-.29zm-1-1v1h-4v-1a2 2 0 1 1 4 0zM12 20a18 18 0 0 1-4.12-2.13A9.3 9.3 0 0 1 5.5 14.77V7a12.3 12.3 0 0 1 2.73-1.17A19.2 19.2 0 0 1 12 5a19.2 19.2 0 0 1 3.77.82A12.3 12.3 0 0 1 18.5 7v5.1c-.16-.05-.33-.08-.5-.1a3 3 0 0 0-3 3v1a1 1 0 0 0-1 1v2.13c-.65.34-1.31.63-2 .87zM21 20h-6v-3h6z" />
    </svg>
  ),
  matrix: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm8-2h8v8h-8v-8zm2 2v4h4v-4h-4z" />
    </svg>
  ),
  // 4-up grid — used for the new "Dashboards" entry, mirrors the v7
  // mockup's sidebar icon for the cameras-dashboard surface.
  dashboards: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 3h8v8H3zm10 0h8v8h-8zM3 13h8v8H3zm10 0h8v8h-8z" />
    </svg>
  ),
  history: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6a7 7 0 1 1 2.05 4.94l-1.42 1.42A9 9 0 1 0 13 3zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" />
    </svg>
  ),
  location: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 0 5z" />
    </svg>
  ),
  // Storefront / workspace tile
  workspace: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21 7l-1.5-3a1 1 0 0 0-.9-.5H5.4a1 1 0 0 0-.9.5L3 7v2a3 3 0 0 0 1.5 2.6V20a1 1 0 0 0 1 1H14a1 1 0 0 0 1-1v-5h3v5a1 1 0 0 0 1 1h.5a1 1 0 0 0 1-1v-8.4A3 3 0 0 0 21 9V7zm-7 7H6.5v-2.1A3 3 0 0 0 9 9h2v5h3zM5 9V7.2L5.9 5.5h12.2L19 7.2V9a1 1 0 0 1-2 0V7h-2v2a1 1 0 0 1-2 0V7h-2v2a1 1 0 0 1-2 0V7H7v2a1 1 0 0 1-2 0z" />
    </svg>
  ),
  nameTag: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20 4v7a1.83 1.83 0 0 1-.41 1L11 20.59 3.41 13 12 4.41A1.83 1.83 0 0 1 13 4h7m0-1h-7a2.78 2.78 0 0 0-1.71.71L2.71 12.29a1 1 0 0 0 0 1.41l7.59 7.59a1 1 0 0 0 1.41 0l8.59-8.59A2.78 2.78 0 0 0 21 11V4a1 1 0 0 0-1-1z" />
      <path d="M17 6a1 1 0 1 1-1 1 1 1 0 0 1 1-1m0-1a2 2 0 1 0 2 2 2 2 0 0 0-2-2z" />
    </svg>
  ),
  rule: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 16H5V5h14zM7 7h10v2H7zm0 4h10v2H7zm0 4h7v2H7z" />
    </svg>
  ),
  group: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5zm0-8a3 3 0 1 1-3 3 3 3 0 0 1 3-3zm9 17v-1a7 7 0 0 0-7-7h-4a7 7 0 0 0-7 7v1h2v-1a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v1z" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 19a9 9 0 1 1 9-9 9 9 0 0 1-9 9zm5-12.5l-7 7L6 11.5 7.5 10l2.5 2.5 5.5-5.5z" />
    </svg>
  ),
  chevronDouble: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6.41 6L5 7.41 9.58 12 5 16.59 6.41 18l6-6-6-6zm7 0L12 7.41 16.58 12 12 16.59 13.41 18l6-6-6-6z" />
    </svg>
  ),
};

const MAIN_NAV_ITEMS: NavItem[] = [
  // "Dashboards" sits above "Live cameras" — operators with saved walls
  // open them first; the unfiltered live grid is the fall-back view.
  { to: "/dashboards", label: "Dashboards", icon: Icon.dashboards },
  { to: "/cameras", label: "Live cameras", icon: Icon.camera },
  { to: "/recordings", label: "Past recordings", icon: Icon.play },
  { to: "/dashboard", label: "My session", icon: Icon.account },
  { to: "/docs", label: "API Docs", icon: Icon.integrations },
];

// "Identity & Access" — single landing for the people-first surface.
// Groups & users is the only entry; Roles and Rules live in the Admin
// section below as standalone destinations, matching v6.
const IDENTITY_NAV_ITEMS: NavItem[] = [
  { to: "/admin/people", label: "Groups & users", icon: Icon.group },
];

// Admin section — secondary surfaces visible to FLEET_ADMIN.
const ADMIN_NAV_ITEMS: NavItem[] = [
  { to: "/admin/roles", label: "Roles", icon: Icon.nameTag },
  { to: "/admin/rules", label: "Rules", icon: Icon.rule },
  { to: "/admin/locations", label: "Locations", icon: Icon.location },
  { to: "/admin/audit", label: "Audit log", icon: Icon.history },
  { to: "/admin/tenant", label: "Workspace settings", icon: Icon.workspace },
];

const PIN_KEY = "reb.ui.prefs.sidebarPinned";

function readSidebarPinned(): boolean {
  try {
    return window.localStorage.getItem(PIN_KEY) === "1";
  } catch {
    return false;
  }
}

function writeSidebarPinned(pinned: boolean): void {
  try {
    window.localStorage.setItem(PIN_KEY, pinned ? "1" : "0");
  } catch {
    /* localStorage unavailable — non-fatal, just skip persistence. */
  }
}

export function AdminLayout(): JSX.Element {
  const { status, currentRole } = useRole();
  const blocked = status === "pending";
  const toast = useToast();
  const [loggingOut, setLoggingOut] = useState<boolean>(false);
  const location = useLocation();
  const { isAdvanced } = useAdvancedMode();
  const [sidebarPinned, setSidebarPinned] = useState<boolean>(() =>
    readSidebarPinned(),
  );

  // Spotlight wiring. We position the spotlight imperatively (rather
  // than via React state) so we can read each link's bounding rect on
  // mousemove without scheduling a render per pixel. The CSS transition
  // on `top` and `height` produces the springy slide; React just sets
  // the destination.
  const sidebarRef = useRef<HTMLElement | null>(null);
  const spotlightRef = useRef<HTMLDivElement | null>(null);
  const lastHoverTargetRef = useRef<HTMLElement | null>(null);

  /**
   * Move the spotlight to sit behind a target link (or the active item
   * when nothing is hovered). When `target` is null we fade it out so
   * the rail is clean while idle. Reads getBoundingClientRect — cheap,
   * but we still gate it through requestAnimationFrame on mousemove.
   */
  const positionSpotlight = useCallback(
    (target: HTMLElement | null): void => {
      const sidebar = sidebarRef.current;
      const spotlight = spotlightRef.current;
      if (!sidebar || !spotlight) return;
      if (!target || !sidebar.contains(target)) {
        spotlight.classList.remove("is-visible");
        return;
      }
      const sr = sidebar.getBoundingClientRect();
      const tr = target.getBoundingClientRect();
      // Account for the sidebar's own scroll offset so the spotlight
      // tracks the link even when the rail has scrolled.
      const top = tr.top - sr.top + sidebar.scrollTop;
      spotlight.style.top = `${top}px`;
      spotlight.style.height = `${tr.height}px`;
      spotlight.classList.add("is-visible");
    },
    [],
  );

  // When the route changes, re-anchor the spotlight to whichever item
  // just became active so the highlighter "lands" on the new page.
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;
    const active = sidebar.querySelector<HTMLElement>(
      ".admin-sidebar__nav a.active",
    );
    positionSpotlight(active);
  }, [location.pathname, positionSpotlight]);

  // Clear stale error toasts when the user navigates to a different page.
  useEffect(() => {
    toast.clearAll();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const caps = useMemo(() => capabilitiesFor(currentRole), [currentRole]);
  const showAdminSection = caps.canSeeAdmin;

  const handleTogglePin = useCallback((): void => {
    setSidebarPinned((prev) => {
      const next = !prev;
      writeSidebarPinned(next);
      return next;
    });
  }, []);

  // rAF-throttled mouse tracking — one rect read per animation frame at
  // most, regardless of how fast the cursor moves down the rail.
  const rafPendingRef = useRef<boolean>(false);
  const handleSidebarMouseMove = useCallback(
    (e: React.MouseEvent<HTMLElement>): void => {
      const target = (e.target as HTMLElement | null)?.closest<HTMLElement>(
        ".admin-sidebar__nav a",
      );
      if (target === lastHoverTargetRef.current) return;
      lastHoverTargetRef.current = target ?? null;
      if (rafPendingRef.current) return;
      rafPendingRef.current = true;
      requestAnimationFrame(() => {
        rafPendingRef.current = false;
        positionSpotlight(lastHoverTargetRef.current);
      });
    },
    [positionSpotlight],
  );

  // When the cursor leaves, send the spotlight back to the active item.
  // If there isn't one (e.g. the user is on /admin/access/something not
  // covered by our nav), the spotlight just fades.
  const handleSidebarMouseLeave = useCallback((): void => {
    lastHoverTargetRef.current = null;
    const sidebar = sidebarRef.current;
    if (!sidebar) return;
    const active = sidebar.querySelector<HTMLElement>(
      ".admin-sidebar__nav a.active",
    );
    positionSpotlight(active);
  }, [positionSpotlight]);

  // Click feedback: layer a darker tint on the spotlight + a scale-pulse
  // on the icon. The classes self-clear on a short timer so they don't
  // persist after the spring settles.
  const handleNavClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>): void => {
      const link = e.currentTarget;
      const spotlight = spotlightRef.current;
      positionSpotlight(link);
      if (spotlight) {
        spotlight.classList.add("is-clicked");
        window.setTimeout(
          () => spotlight.classList.remove("is-clicked"),
          360,
        );
      }
      // Force a reflow so the animation restarts even if the user
      // re-clicks the same link mid-animation.
      link.classList.remove("is-click-pulse");
      void link.offsetWidth;
      link.classList.add("is-click-pulse");
      window.setTimeout(() => link.classList.remove("is-click-pulse"), 420);
    },
    [positionSpotlight],
  );

  const handleLogout = useCallback(async (): Promise<void> => {
    setLoggingOut(true);
    try {
      await api.post("/api/session/logout", undefined, {
        skipActingRole: true,
      });
    } catch (err) {
      // Surface the error but still proceed with local cleanup — the
      // session is effectively dead from the user's perspective even if
      // the backend delete failed.
      const message =
        err instanceof ApiError
          ? err.message
          : "Logout request failed";
      toast.danger({ title: "Logout error", description: message });
    } finally {
      clearCurrentRole();
      // Full page navigation (not React Router) to clear all in-memory
      // state (SessionProvider, RoleProvider context, etc.).
      window.location.assign("/signin");
    }
  }, [toast]);

  const renderNavItem = (item: NavItem): JSX.Element => (
    <li key={item.to}>
      <NavLink
        to={item.to}
        // `end` keeps the People landing from staying active when the
        // user drills into /admin/people/groups/:id.
        end={item.to === "/admin/people"}
        tabIndex={blocked ? -1 : undefined}
        onClick={(e) => {
          if (blocked) {
            e.preventDefault();
            return;
          }
          handleNavClick(e);
        }}
        aria-disabled={blocked || undefined}
        title={item.label}
      >
        <span className="admin-sidebar__nav-icon" aria-hidden="true">
          {item.icon}
        </span>
        <span className="admin-sidebar__nav-label">{item.label}</span>
      </NavLink>
    </li>
  );

  return (
    <div
      className={
        sidebarPinned ? "admin-layout is-sidebar-pinned" : "admin-layout"
      }
    >
      <header className="admin-topbar" role="banner">
        <div className="admin-topbar__brand" aria-label="Ring Business">
          {/* Authentic Ring wordmark — SVG paths from ring.com. The dot
              over the "i" is tinted Ring blue via a CSS class. */}
          <svg
            className="admin-topbar__wordmark"
            viewBox="0 0 77 50"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              className="letters"
              d="M76.9997 38.973V19.2586C76.9997 19.2555 76.9997 19.2524 76.9997 19.2501C76.9935 13.5729 72.3712 8.95283 66.6855 8.95129C61.0044 8.95283 56.3805 13.5767 56.3782 19.2617V28.6288C56.3782 34.3137 61.0036 38.9391 66.6885 38.9391C69.1356 38.9391 71.4672 38.0852 73.3237 36.5213V38.9969C73.3206 42.5905 70.2036 45.6266 66.5168 45.6266C64.4817 45.6266 62.4543 44.701 61.2246 43.2119C61.2207 43.2072 61.2169 43.2034 61.2138 43.1988C60.8627 42.776 60.3491 42.5343 59.8001 42.5343C58.7868 42.5343 57.9621 43.3589 57.9621 44.3723C57.9621 44.7919 58.1084 45.2031 58.3733 45.5296C58.3763 45.5342 58.3802 45.5388 58.3833 45.5427C60.3221 47.8966 63.3629 49.3018 66.5168 49.3018C72.2973 49.3018 76.9997 44.6764 76.9997 38.9915C77.0005 38.9845 76.9997 38.9792 76.9997 38.973ZM73.3237 19.2617V28.6288C73.3237 32.287 70.3476 35.2639 66.6885 35.2639C63.0295 35.2639 60.0534 32.2878 60.0534 28.6264V19.2617C60.0534 15.6034 63.0295 12.6265 66.6885 12.6265C70.3476 12.6265 73.3237 15.6026 73.3237 19.2617Z"
            />
            <path
              className="letters"
              d="M40.6632 8.95056C34.9736 8.95056 30.342 13.5783 30.3374 19.2671C30.3374 19.2702 30.3374 19.2732 30.3374 19.2763V36.2464C30.3374 37.2613 31.1628 38.0868 32.1777 38.0868C33.1926 38.0868 34.018 37.2613 34.018 36.2457V19.2763C34.018 15.6126 36.9987 12.632 40.6624 12.632C44.3261 12.632 47.3068 15.6126 47.3068 19.2763V36.2464C47.3068 37.2613 48.1322 38.0868 49.1471 38.0868C50.1619 38.0868 50.9874 37.2613 50.9874 36.2464V19.2763V19.2725C50.9866 13.5814 46.3543 8.95056 40.6632 8.95056Z"
            />
            <path
              className="letters"
              d="M15.2546 10.8494C15.2484 10.8448 15.2422 10.8402 15.2361 10.8355C15.2353 10.8348 15.203 10.8132 15.2022 10.8124C13.4643 9.59582 11.4215 8.9521 9.32014 8.9521C9.31937 8.9521 9.3186 8.9521 9.31783 8.9521C9.30859 8.95133 9.29396 8.95056 9.28472 8.95056C3.907 8.9521 0.00230988 13.2926 0.000769875 19.2501C-1.29226e-07 19.2586 0 19.2679 0 19.2763V36.2464C0 37.2613 0.825445 38.0868 1.84031 38.0868C2.85518 38.0868 3.68062 37.2613 3.68062 36.2464V19.2763C3.68062 15.24 5.8813 12.632 9.2878 12.632C10.6738 12.632 12.0013 13.0539 13.1278 13.8532C13.1347 13.8586 13.1424 13.8632 13.1494 13.867C13.4543 14.0726 13.8085 14.1804 14.1758 14.1804C15.1907 14.1804 16.0169 13.355 16.0169 12.3394C16.0169 11.7511 15.7327 11.1951 15.2546 10.8494Z"
            />
            <path
              className="letters"
              d="M22.3355 10.5798C21.323 10.5798 20.4991 11.4037 20.4991 12.4163V36.2556C20.4991 37.2682 21.323 38.0921 22.3355 38.0921C23.3481 38.0921 24.172 37.2682 24.172 36.2556V12.4163C24.172 11.403 23.3481 10.5798 22.3355 10.5798Z"
            />
            <path
              className="i-dot"
              d="M22.3055 0C20.7146 0 19.4203 1.29438 19.4203 2.88521C19.4203 4.47604 20.7146 5.76965 22.3055 5.76965C23.8963 5.76965 25.1899 4.47527 25.1899 2.88521C25.1907 1.29438 23.8963 0 22.3055 0Z"
            />
          </svg>
          <span className="admin-topbar__brand-sub">Business</span>
        </div>
        <div className="admin-topbar__actions">
          {/* Workspace pill — appears only after WorkspaceProvider has
              resolved a tenant, so it's invisible on the upstream-only
              flow where /api/session 404s. */}
          <WorkspaceIndicator />
          <RoleSwitcher />
          {isAdvanced && (
            <span
              className="adv-badge"
              aria-label="Advanced mode is on"
              title="Advanced mode is on"
            >
              ADV
            </span>
          )}
          <AvatarMenu onLogout={handleLogout} loggingOut={loggingOut} />
        </div>
      </header>
      <nav
        className="admin-sidebar"
        aria-label="Primary"
        aria-hidden={blocked || undefined}
        ref={sidebarRef}
        onMouseMove={handleSidebarMouseMove}
        onMouseLeave={handleSidebarMouseLeave}
      >
        {/* Sliding spotlight: a single absolutely-positioned element
            whose top/height are written imperatively to follow the
            hovered (or active) link. CSS handles the spring. */}
        <div
          className="admin-sidebar__spotlight"
          ref={spotlightRef}
          aria-hidden="true"
        />
        <button
          type="button"
          className="admin-sidebar__pin"
          aria-label={
            sidebarPinned ? "Collapse sidebar" : "Keep sidebar open"
          }
          aria-pressed={sidebarPinned}
          title={sidebarPinned ? "Collapse sidebar" : "Keep sidebar open"}
          onClick={handleTogglePin}
        >
          {Icon.chevronDouble}
        </button>
        <ul className="admin-sidebar__nav">
          {MAIN_NAV_ITEMS.map(renderNavItem)}

          {showAdminSection && (
            <>
              <li className="admin-sidebar__section-label" aria-hidden="true">
                Identity &amp; Access
              </li>
              {IDENTITY_NAV_ITEMS.map(renderNavItem)}
              <li className="admin-sidebar__section-label" aria-hidden="true">
                Admin
              </li>
              {ADMIN_NAV_ITEMS.map(renderNavItem)}
            </>
          )}
        </ul>
        <div className="admin-sidebar__footer">
          <MockedDepsBanner />
        </div>
      </nav>
      <main
        className="admin-content"
        role="main"
        aria-hidden={blocked || undefined}
      >
        <div className="admin-content__inner">
          {/* Post-install welcome banner — self-hides when no workspace
              is loaded or when the admin has dismissed it. */}
          <WelcomeBanner />
          {/* Contain page-level render crashes to this region so a thrown
              exception (e.g. an undefined rule.group_match deref) shows a
              card here instead of blanking the entire SPA. */}
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
      <RolePickerModal />
      {/* Workspace disconnected — non-dismissible; only renders when the
          BFF reports the tenant has been disconnected (force-unlink, Ring
          webhook revoke). */}
      <WorkspaceDisconnectedModal />
    </div>
  );
}
